import { SEQUENCE_END } from '../internalTimeline';

export type V3SceneId =
  | 'prompt'
  | 'desktop'
  | 'desktop-spot'
  | 'mobile'
  | 'mobile-spot'
  | 'signage'
  | 'email'
  | 'report';

export type V3Format = 'wide' | 'narrow';

export type V3CaptionKey = 'prompt' | 'desktop' | 'mobile' | 'signage' | 'email' | 'report';

export interface V3SceneDef {
  id: V3SceneId;
  format: V3Format;
  caption: V3CaptionKey;
  /** How long the scene holds fully settled, before the next transition begins. */
  holdMs: number;
}

/** Beat between hold end and the next flip; the cross-fade itself overlaps the
   incoming scene's hold, so this stays short to avoid reading as dead time. */
export const V3_TRANSITION_MS = 280;

/*
 * The eight Figma states ("Home page - Cue Conductor - Take over", node
 * 2108:1552) in approved order. Every frame is 1444x762 with a 557px-tall
 * frosted panel, so only the panel width morphs between formats — that is the
 * whole Unily-style gesture. The spotlight states share their base scene's
 * layer and dim everything except the "Needs attention" card via --v3-q.
 */
export const V3_SCENES: readonly V3SceneDef[] = [
  { id: 'prompt', format: 'wide', caption: 'prompt', holdMs: SEQUENCE_END + 350 },
  { id: 'desktop', format: 'wide', caption: 'desktop', holdMs: 2400 },
  { id: 'desktop-spot', format: 'wide', caption: 'desktop', holdMs: 1500 },
  { id: 'mobile', format: 'narrow', caption: 'mobile', holdMs: 2400 },
  { id: 'mobile-spot', format: 'narrow', caption: 'mobile', holdMs: 1500 },
  { id: 'signage', format: 'wide', caption: 'signage', holdMs: 2500 },
  { id: 'email', format: 'narrow', caption: 'email', holdMs: 2600 },
  { id: 'report', format: 'wide', caption: 'report', holdMs: 3600 },
];

/* ------------------------------------------------- internal scene timelines */

export type V3Ease = 'linear' | 'outCubic' | 'inOutCubic' | 'outQuint';

export type V3CounterFormat = 'percent' | 'thousands';

export interface V3Cue {
  scene: V3SceneId;
  /** Property suffix: the engine writes `--v3r-<scene>-<id>[-<i>]`. */
  id: string;
  /** Window in the scene's local assembly [0, 1]. For a group, the envelope. */
  from: number;
  to: number;
  ease?: V3Ease;
  count?: number;
  stagger?: number;
  /** A number counted up while the cue runs, written to [data-v3-count=<id>]. */
  counter?: { from: number; to: number; format: V3CounterFormat };
  /** False for counter-only cues, which have no stylesheet consumer. */
  write?: boolean;
}

/** Assembly duration per scene, played after the layer's fade-in delay. */
export const V3_ASSEMBLE_MS: Partial<Record<V3SceneId, number>> = {
  desktop: 1700,
  mobile: 1500,
  signage: 1800,
  email: 1900,
  report: 2800,
};

export const V3_ENTER_DELAY_MS = 200;

/*
 * Adapted 1:1 from V2's INTERNAL map (internalTimeline.ts): same cue order,
 * same relative windows and eases, retimed into each scene's local assembly.
 * Desktop/Mobile split V2's single 'base' raster into the new design's cards
 * as a staggered group, keeping V2's base -> card -> rows arc.
 */
export const V3_CUES: readonly V3Cue[] = [
  { scene: 'desktop', id: 'base', from: 0.0, to: 0.55, count: 4, stagger: 0.09 },
  { scene: 'desktop', id: 'card', from: 0.3, to: 0.75 },
  { scene: 'desktop', id: 'rows', from: 0.55, to: 0.98, count: 2, stagger: 0.14 },

  { scene: 'mobile', id: 'base', from: 0.0, to: 0.55, count: 4, stagger: 0.09 },
  { scene: 'mobile', id: 'card', from: 0.3, to: 0.75 },
  { scene: 'mobile', id: 'rows', from: 0.55, to: 0.98, count: 2, stagger: 0.14 },

  { scene: 'signage', id: 'status', from: 0.0, to: 0.26 },
  { scene: 'signage', id: 'photo', from: 0.14, to: 0.46 },
  { scene: 'signage', id: 'qr', from: 0.32, to: 0.56 },
  { scene: 'signage', id: 'rule', from: 0.42, to: 0.64, ease: 'inOutCubic' },
  { scene: 'signage', id: 'heading', from: 0.52, to: 0.76 },
  { scene: 'signage', id: 'body', from: 0.6, to: 0.84 },
  { scene: 'signage', id: 'ticker', from: 0.78, to: 1.0 },

  { scene: 'email', id: 'logo', from: 0.0, to: 0.16 },
  { scene: 'email', id: 'card', from: 0.06, to: 0.32 },
  { scene: 'email', id: 'photo', from: 0.18, to: 0.42 },
  { scene: 'email', id: 'alert', from: 0.3, to: 0.48 },
  { scene: 'email', id: 'subject', from: 0.36, to: 0.54 },
  { scene: 'email', id: 'body', from: 0.42, to: 0.6 },
  { scene: 'email', id: 'button', from: 0.48, to: 0.66 },
  { scene: 'email', id: 'section', from: 0.58, to: 0.74 },
  { scene: 'email', id: 'tiles', from: 0.64, to: 0.94, count: 4, stagger: 0.055 },
  { scene: 'email', id: 'more', from: 0.9, to: 1.0 },

  { scene: 'report', id: 'shell', from: 0.0, to: 0.14 },
  { scene: 'report', id: 'heading', from: 0.12, to: 0.28 },
  { scene: 'report', id: 'gauge-card', from: 0.2, to: 0.34 },
  { scene: 'report', id: 'gauge-draw', from: 0.28, to: 0.54, ease: 'outQuint' },
  { scene: 'report', id: 'gauge-value', from: 0.3, to: 0.4 },
  {
    scene: 'report',
    id: 'gauge-count',
    from: 0.3,
    to: 0.56,
    ease: 'outQuint',
    write: false,
    counter: { from: 0, to: 86, format: 'percent' },
  },
  { scene: 'report', id: 'gauge-caption', from: 0.48, to: 0.6 },
  { scene: 'report', id: 'divider', from: 0.5, to: 0.62, ease: 'inOutCubic' },
  { scene: 'report', id: 'dept-label', from: 0.5, to: 0.62 },
  { scene: 'report', id: 'bar-rows', from: 0.52, to: 0.74, count: 5, stagger: 0.035 },
  { scene: 'report', id: 'bars', from: 0.56, to: 0.82, count: 5, stagger: 0.04 },
  { scene: 'report', id: 'open-card', from: 0.6, to: 0.72 },
  { scene: 'report', id: 'open-value', from: 0.62, to: 0.72 },
  {
    scene: 'report',
    id: 'open-count',
    from: 0.62,
    to: 0.78,
    ease: 'outQuint',
    write: false,
    counter: { from: 0, to: 94, format: 'percent' },
  },
  { scene: 'report', id: 'completed-card', from: 0.66, to: 0.78 },
  { scene: 'report', id: 'completed-value', from: 0.68, to: 0.78 },
  {
    scene: 'report',
    id: 'completed-count',
    from: 0.68,
    to: 0.86,
    ease: 'outQuint',
    write: false,
    counter: { from: 0, to: 12986, format: 'thousands' },
  },
  { scene: 'report', id: 'mini-bar', from: 0.74, to: 0.88, ease: 'inOutCubic' },
  { scene: 'report', id: 'observations', from: 0.78, to: 0.9 },
  { scene: 'report', id: 'obs-items', from: 0.8, to: 0.94, count: 2, stagger: 0.05 },
  { scene: 'report', id: 'reminder', from: 0.88, to: 0.97 },
  { scene: 'report', id: 'composer', from: 0.92, to: 1.0 },
];
